import DOMPurify from 'dompurify';

import {
  CITATION_TEXT_PATTERN,
  FOOTNOTE_CITATION_PATTERN,
  getPublicCitationUrl,
} from './sources.utils';

import type { CitationSource } from '../types';

export type CopyFormat = 'rich-text' | 'markdown';

const COPY_EXCLUDE_SELECTOR = '[data-copy-exclude]';

/** Both inline and footnote citation chips carry the source url they point at. */
const CITATION_URL_SELECTOR = '[data-citation-url]';

/** `[[N]](url)`, the form a `[source](url)` citation is streamed in as. */
const NUMBERED_CITATION_PATTERN = /\[\[\d+\]\]\([^)[\]]+\)/g;

/** Every citation form that can appear in a message's markdown. */
const CITATION_PATTERNS = [
  FOOTNOTE_CITATION_PATTERN,
  CITATION_TEXT_PATTERN,
  NUMBERED_CITATION_PATTERN,
];

/**
 * Replaces each citation matched by `pattern` and changes nothing else, since
 * in markdown spacing carries meaning (list nesting, hard line breaks, code).
 * A citation replaced with '' also takes the space that separated it from the
 * word before it, or, at the start of an indented line, the space after it.
 */
function replaceCitations(
  text: string,
  pattern: RegExp,
  replace: (match: RegExpExecArray) => string,
): string {
  const citation = new RegExp(pattern.source, 'g');
  let result = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = citation.exec(text)) !== null) {
    result += text.slice(lastIndex, match.index);
    lastIndex = citation.lastIndex;

    const replacement = replace(match);
    if (replacement) {
      result += replacement;
      continue;
    }

    let end = result.length;
    while (result[end - 1] === ' ' || result[end - 1] === '\t') end--;

    if (end === 0 || result[end - 1] === '\n') {
      while (text[lastIndex] === ' ' || text[lastIndex] === '\t') lastIndex++;
    } else {
      result = result.slice(0, end);
    }
  }

  return result + text.slice(lastIndex);
}

/** Removes every citation, leaving the rest of the markdown as written. */
export function stripCitations(text: string): string {
  return CITATION_PATTERNS.reduce(
    (stripped, pattern) => replaceCitations(stripped, pattern, () => ''),
    text,
  );
}

/**
 * Converts [^N] footnote citations to inline [[N]](url) links.
 * Sources without a public URL or with no matching index are removed.
 */
export function resolveFootnoteCitationsForPlaintext(
  text: string,
  sources: CitationSource[],
): string {
  return replaceCitations(text, FOOTNOTE_CITATION_PATTERN, ([, numStr]) => {
    const source = sources[parseInt(numStr, 10) - 1];
    const url = source ? getPublicCitationUrl(source) : undefined;
    return url ? `[[${numStr}]](${url})` : '';
  });
}

/**
 * Extracts the snippet ID from a curated snippet URL.
 * Returns null if the URL is not a snippet URL.
 */
export function extractSnippetId(url: string): string | null {
  const snippetMatch = url.match(/\/curated-snippets\/([^/]+)\//);
  return snippetMatch ? snippetMatch[1] : null;
}

/**
 * Extracts all snippet IDs from an array of citation sources.
 */
export function extractSnippetIds(sources: CitationSource[]): string[] {
  return sources
    .map((source) => extractSnippetId(source.url))
    .filter((id): id is string => id !== null);
}

/**
 * Inline styles applied per tag before the copied HTML reaches the clipboard.
 *
 * The rendered content styles itself with CSS classes (streamdown's Tailwind
 * utilities plus a Preflight reset that zeroes heading weight). Google Docs
 * discards external stylesheets on paste and honors only semantic tags and
 * inline `style` attributes, so headings flatten and tables lose their borders.
 * Restating the key styling inline lets the paste keep heading hierarchy, bold,
 * list indentation, and bordered tables.
 */
const CLIPBOARD_TAG_STYLES: Record<string, string> = {
  h1: 'font-size:20pt;font-weight:700;margin:16px 0 8px;',
  h2: 'font-size:16pt;font-weight:700;margin:14px 0 6px;',
  h3: 'font-size:13pt;font-weight:700;margin:12px 0 6px;',
  h4: 'font-size:11pt;font-weight:700;margin:10px 0 4px;',
  h5: 'font-size:11pt;font-weight:700;margin:10px 0 4px;',
  h6: 'font-size:11pt;font-weight:700;margin:10px 0 4px;',
  strong: 'font-weight:700;',
  b: 'font-weight:700;',
  table: 'border-collapse:collapse;border:1px solid #bfbfbf;',
  th: 'border:1px solid #bfbfbf;padding:6px 9px;text-align:left;font-weight:700;background-color:#f3f3f3;',
  td: 'border:1px solid #bfbfbf;padding:6px 9px;text-align:left;vertical-align:top;',
  ul: 'margin:6px 0;padding-left:24px;',
  ol: 'margin:6px 0;padding-left:24px;',
  li: 'margin:2px 0;',
  blockquote:
    'border-left:3px solid #bfbfbf;padding-left:12px;margin:8px 0;color:#555;',
};

/**
 * Selector matching the element streamdown renders for markdown bold: a
 * `<span class="font-semibold">` tagged with this data attribute. No `<strong>`
 * element is ever produced, so bold has to be restored before the tag styles
 * above can reach it.
 */
const STREAMDOWN_STRONG_SELECTOR = '[data-streamdown="strong"]';

/**
 * Prepends inline styling to an element, keeping any existing inline style so a
 * later, more specific declaration still wins on conflict.
 */
function prependInlineStyle(el: Element, style: string): void {
  const existing = el.getAttribute('style');
  el.setAttribute('style', existing ? `${style}${existing}` : style);
}

/**
 * Replaces streamdown's bold spans with `<strong>` elements, keeping their
 * children. Bold then carries both a semantic tag and, once the tag styles are
 * applied, an inline weight, so it survives whichever of the two a paste target
 * honors.
 */
function restoreBoldTags(root: HTMLElement): void {
  root.querySelectorAll(STREAMDOWN_STRONG_SELECTOR).forEach((el) => {
    const strong = el.ownerDocument.createElement('strong');
    strong.append(...el.childNodes);
    el.replaceWith(strong);
  });
}

/**
 * Rewrites class-based styling into inline styles so pasted content keeps its
 * formatting in editors that ignore external CSS, such as Google Docs.
 */
function applyClipboardInlineStyles(root: HTMLElement): void {
  restoreBoldTags(root);

  Object.entries(CLIPBOARD_TAG_STYLES).forEach(([tag, style]) => {
    root.querySelectorAll(tag).forEach((el) => prependInlineStyle(el, style));
  });
}

/**
 * Replaces each citation chip with a `[N]` link to its source's public URL,
 * numbered by the source's position in `sources`. A chip with no public URL is
 * dropped, as the markdown copy drops it.
 */
function replaceCitationsWithLinks(
  root: HTMLElement,
  sources: CitationSource[],
): void {
  root.querySelectorAll(CITATION_URL_SELECTOR).forEach((el) => {
    const url = el.getAttribute('data-citation-url');
    const index = sources.findIndex((source) => source?.url === url);
    const publicUrl =
      index === -1 ? undefined : getPublicCitationUrl(sources[index]);

    if (!publicUrl) {
      el.remove();
      return;
    }

    const link = el.ownerDocument.createElement('a');
    link.setAttribute('href', publicUrl);
    link.textContent = `[${index + 1}]`;
    el.replaceWith(link);
  });
}

/**
 * Sanitizes HTML content for copying by removing UI elements and potentially harmful content.
 * Uses DOMPurify for secure sanitization while preserving inline styles and links.
 *
 * Passing `sources` keeps the citations as linked `[N]` markers instead of
 * dropping them, so a pasted answer says which claim came from which source.
 */
export function sanitizeHtmlForCopy(
  html: string,
  sources?: CitationSource[],
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  if (sources) {
    replaceCitationsWithLinks(doc.body, sources);
  }

  doc.querySelectorAll(COPY_EXCLUDE_SELECTOR).forEach((el) => el.remove());

  applyClipboardInlineStyles(doc.body);

  return DOMPurify.sanitize(doc.body.innerHTML, {
    ADD_ATTR: ['style', 'href', 'target'],
    ADD_TAGS: ['style'],
  }) as string;
}

/**
 * Writes each linked `[N]` citation as `[[N]](url)`, the form the markdown copy
 * uses, so a plain-text paste keeps the link.
 */
function writeCitationLinksAsMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('a[href]').forEach((link) => {
    const label = link.textContent ?? '';
    if (!/^\[\d+\]$/.test(label)) return;
    link.replaceWith(
      doc.createTextNode(`[${label}](${link.getAttribute('href')})`),
    );
  });

  return doc.body.innerHTML;
}

/**
 * Extracts plain text from HTML content.
 * Preserves line breaks and removes extra whitespace.
 */
export function extractPlainTextFromHtml(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll(COPY_EXCLUDE_SELECTOR).forEach((el) => el.remove());

  return doc.body.innerText || doc.body.textContent || '';
}

/**
 * Checks if the browser supports the ClipboardItem API for rich text copying.
 */
export function supportsRichTextCopy(): boolean {
  return (
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function'
  );
}

/**
 * Copies rich text (HTML) to clipboard with plain text fallback.
 */
async function copyRichTextToClipboard(
  htmlContent: string,
  plainTextFallback: string,
): Promise<void> {
  const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
  const textBlob = new Blob([plainTextFallback], { type: 'text/plain' });

  await navigator.clipboard.write([
    new ClipboardItem({
      'text/html': htmlBlob,
      'text/plain': textBlob,
    }),
  ]);
}

/**
 * Copies a message to the clipboard in the requested format.
 *
 * Rich text writes both an HTML and a plain-text flavour so the paste target can
 * pick the one it understands. Markdown writes plain text only: offering an HTML
 * flavour alongside it would let editors such as Google Docs take the rendered
 * version instead of the markdown the option promises.
 *
 * Both formats link each citation inline to its public URL: rich text as a
 * linked `[N]`, markdown as `[[N]](url)`. Neither appends a sources list.
 */
export async function copyMessageToClipboard(
  text: string,
  sources: CitationSource[],
  format: CopyFormat,
  includeSources: boolean,
  htmlContent?: string,
): Promise<void> {
  if (format === 'markdown') {
    await navigator.clipboard.writeText(
      includeSources
        ? resolveFootnoteCitationsForPlaintext(text, sources)
        : stripCitations(text),
    );
    return;
  }

  // With sources on, the html keeps linked `[N]` markers and the plain flavour,
  // derived from that same html, writes them as `[[N]](url)`.
  const sanitizedHtml = htmlContent
    ? sanitizeHtmlForCopy(htmlContent, includeSources ? sources : undefined)
    : '';

  if (!htmlContent) {
    await navigator.clipboard.writeText(
      includeSources
        ? resolveFootnoteCitationsForPlaintext(text, sources)
        : stripCitations(text),
    );
    return;
  }

  const plainText = includeSources
    ? extractPlainTextFromHtml(writeCitationLinksAsMarkdown(sanitizedHtml))
    : stripCitations(extractPlainTextFromHtml(sanitizedHtml));

  if (!supportsRichTextCopy()) {
    await navigator.clipboard.writeText(plainText);
    return;
  }

  await copyRichTextToClipboard(sanitizedHtml, plainText);
}
